import { ArrowLeft, Moon, Sun, Monitor, Info, Copy, Check } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTheme } from '@/contexts/ThemeContext'
import { useUserData } from '@/hooks/useCpn'
import { authService } from '@/services/auth'
import { logger } from '@/utils/logger'

interface SettingsScreenProps {
  onBack: () => void
}
const themeOptions = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

const SettingsScreen = ({ onBack }: SettingsScreenProps) => {
  const { theme, setTheme } = useTheme()
  const authData = authService.getAuthData()
  const userEmail = authData?.email || 'user@example.com'
  const { data: userData } = useUserData()
  const [copied, setCopied] = useState(false)

  const referralCode = userData?.referralCode
  const referralLink = referralCode
    ? `https://gmt.paytrie.com/signup?ref=${referralCode}`
    : ''

  const handleCopyReferral = async () => {
    if (!referralLink) return
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      logger.error('Failed to copy referral link', error)
    }
  }

  return (
    <div className="flex flex-col h-[600px] bg-white dark:bg-gray-900">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Settings
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Customize your experience
            </p>
          </div>
        </div>

        {/* Account Info */}
        <Card className="p-4 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                {userEmail.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                {userEmail}
              </h3>
            </div>
          </div>
        </Card>

        {/* Referral */}
        {referralCode && (
          <Card className="p-4 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              Referral
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Your code
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {referralCode}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Credits
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {userData?.referralCreditBalance ?? 0}
                </span>
              </div>
              <div className="pt-2">
                <button
                  onClick={handleCopyReferral}
                  className="w-full flex items-center justify-center space-x-2 p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {copied ? 'Copied!' : 'Copy referral link'}
                  </span>
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Appearance */}
        <Card className="p-4 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="font-medium text-gray-900 dark:text-white mb-3">
            Appearance
          </h3>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Theme
              </label>
              <div className="grid grid-cols-3 gap-2">
                {themeOptions.map((option) => {
                  const Icon = option.icon
                  const isSelected = theme === option.value

                  return (
                    <button
                      key={option.value}
                      onClick={() => setTheme(option.value as 'light' | 'dark')}
                      className={`
                        flex items-center justify-center space-x-2 p-3 rounded-lg border transition-colors
                        ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {option.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* About */}
        <Card className="p-4 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="font-medium text-gray-900 dark:text-white mb-3">
            About
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Version
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    1.0.0
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default SettingsScreen
